import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";

export interface CoachLeaderScheduledCall {
  coachCallId: string;
  title: string;
  startsAt: string;
  status: "scheduled" | "cancelled" | "completed";
}

export interface CoachLeaderCallSchedule {
  observedAt: string;
  meetings: CoachLeaderScheduledCall[];
}

interface ParticipantRow {
  coach_call_id: string;
}

interface CallRow {
  id: string;
  title: string;
  scheduled_start: string;
  status: "scheduled" | "cancelled" | "completed" | null;
}

interface ParticipantTable {
  select(columns: "coach_call_id"): {
    eq(column: "leader_id", value: string): {
      limit(count: number): Promise<{ data: ParticipantRow[] | null; error: unknown }>;
    };
  };
}

interface CallsTable {
  select(columns: "id, title, scheduled_start, status"): {
    in(column: "id", values: string[]): {
      eq(column: "coach_id", value: string): Promise<{ data: CallRow[] | null; error: unknown }>;
    };
  };
}

export function useCoachLeaderCallSchedule({
  coachId,
  leaderId,
  relationshipRevision,
  accessGeneration,
  enabled,
}: {
  coachId?: string | null;
  leaderId?: string | null;
  relationshipRevision?: string | null;
  accessGeneration: string;
  enabled: boolean;
}) {
  return useQuery({
    queryKey: [
      "coach-calls",
      coachId,
      "leader-detail",
      leaderId,
      relationshipRevision,
      accessGeneration,
    ],
    enabled: Boolean(enabled && coachId && leaderId),
    queryFn: async (): Promise<CoachLeaderCallSchedule> => {
      if (!coachId || !leaderId) throw new Error("Coach and leader identity are required.");

      const participantTable = supabase.from("coach_call_participants") as unknown as ParticipantTable;
      const callsTable = supabase.from("coach_calls") as unknown as CallsTable;
      const participantResult = await participantTable
        .select("coach_call_id")
        .eq("leader_id", leaderId)
        .limit(200);
      if (participantResult.error) throw participantResult.error;

      const callIds = [...new Set((participantResult.data ?? []).map((row) => row.coach_call_id).filter(Boolean))];
      const observedAt = new Date().toISOString();
      if (callIds.length === 0) return { observedAt, meetings: [] };

      const callResult = await callsTable
        .select("id, title, scheduled_start, status")
        .in("id", callIds)
        .eq("coach_id", coachId);
      if (callResult.error) throw callResult.error;

      return {
        observedAt,
        meetings: (callResult.data ?? []).map((call) => ({
          coachCallId: call.id,
          title: call.title,
          startsAt: call.scheduled_start,
          status: call.status ?? "scheduled",
        })),
      };
    },
    retry: false,
    refetchOnMount: "always",
    refetchOnWindowFocus: true,
  });
}
